import axios from "../axiosInstance"

export interface EmployeeOutreachParams {
  companyIds?: number[]
  source?: "hotlist"
  name?: string
  email?: string
  jobType?: string[]
  seniority?: string[]
  startDateFrom?: string
  startDateTo?: string
  endDateFrom?: string
  endDateTo?: string
  tenureMinMonths?: number
  tenureMaxMonths?: number
  status?: "current" | "departed"
  hasEmail?: "yes" | "no"
  page?: number
  pageSize?: number
}

export interface EmployeeOutreachRow {
  eventId: number
  name: string | null
  linkedinUrl: string | null
  companyName: string
  companyId: number | null
  jobTitle: string | null
  startDate: string | null
  endDate: string | null
  status: "current" | "departed"
  seniorityTier: string
  jobType: string | null
  email: string | null
  location: string | null
  tenureMonths: number | null
  excludeMarketing: boolean
  blocked: boolean
}

export interface EmployeeOutreachResponse {
  rows: EmployeeOutreachRow[]
  total: number
  page: number
  pageSize: number
}

function buildQueryParams(
  params: EmployeeOutreachParams
): Record<string, string | number> {
  const q: Record<string, string | number> = {}
  if (params.companyIds?.length) q.companyIds = params.companyIds.join(",")
  if (params.source) q.source = params.source
  if (params.name) q.name = params.name
  if (params.email) q.email = params.email
  if (params.jobType?.length) q.jobType = params.jobType.join(",")
  if (params.seniority?.length) q.seniority = params.seniority.join(",")
  if (params.startDateFrom) q.startDateFrom = params.startDateFrom
  if (params.startDateTo) q.startDateTo = params.startDateTo
  if (params.endDateFrom) q.endDateFrom = params.endDateFrom
  if (params.endDateTo) q.endDateTo = params.endDateTo
  if (params.tenureMinMonths != null) q.tenureMinMonths = params.tenureMinMonths
  if (params.tenureMaxMonths != null) q.tenureMaxMonths = params.tenureMaxMonths
  if (params.status) q.status = params.status
  if (params.hasEmail) q.hasEmail = params.hasEmail
  if (params.page) q.page = params.page
  if (params.pageSize) q.pageSize = params.pageSize
  return q
}

export const queryEmployeeOutreach = async (
  params: EmployeeOutreachParams
): Promise<EmployeeOutreachResponse> => {
  const res = await axios.get("/employee-outreach", {
    params: buildQueryParams(params),
  })
  const { data } = res
  if (!data.success) throw new Error(data.message || "Failed to query employees")
  return {
    rows: data.rows,
    total: data.total,
    page: data.page,
    pageSize: data.pageSize,
  }
}

export const getOutreachCompanies = async (): Promise<
  { id: number; name: string }[]
> => {
  const res = await axios.get("/employee-outreach/companies")
  const { data } = res
  if (!data.success) throw new Error(data.message || "Failed to fetch companies")
  return data.companies
}

export const downloadEmployeeOutreachCsv = async (
  params: EmployeeOutreachParams
): Promise<void> => {
  const res = await axios.get("/employee-outreach/export", {
    params: buildQueryParams(params),
    responseType: "blob",
  })
  const url = window.URL.createObjectURL(res.data)
  const link = document.createElement("a")
  link.href = url
  const today = new Date().toISOString().slice(0, 10)
  link.download = `employee-outreach-${today}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
